<template>
  <div :key="reload">
    <div>
      <empty-webhook-view class="p-5" v-if="!webhook.secret && !webhook.url && !editMode" @click="createWebhook"/>
      <div v-else class="row p-5">
        <div class="col-10">
          <div v-if="editMode">
            <errors-alert :errors="errors"></errors-alert>
            <form @submit.prevent="promiseUpdateWebhook = saveWebhook()">
              <div class="row form-group">
                <label for="webhook-url" class="col-form-label col-4 col-xl-3 text-muted">
                  Webhook URL:
                  <span id="webhook_url_info_edit"
                        v-b-popover:webhook_url_info_edit.html.hover.right="webhookUrlPopoverText">
                  <icon name="info-circle"></icon>
                </span>
                </label>
                <div class="col-7 col-xl-8">
                  <input type="text"
                         id="webhook-url"
                         class="form-control"
                         :class="{'is-invalid': showWebhookUrlInvalidMessage}"
                         v-model="webhook.url">
                  <div v-show="showWebhookUrlInvalidMessage" class="invalid-feedback">
                    Please provide a valid URL.
                  </div>
                </div>
                <div class="col-1 d-flex align-items-baseline col-form-label"
                     v-if="channel.webhook && channel.webhook.url">
                  <a href="#" role="button" @click="$root.$emit('bv::show::modal', modalId)">
                    <icon class="btn-icon text-danger" name="trash" scale="1.0"/>
                  </a>
                </div>
              </div>

              <div class="row form-group">
                <label for="webhook-secret" class="col-4 col-xl-3 col-form-label text-muted">
                  Secret Token:
                  <span id="secret_token_info_edit"
                        v-b-popover:secret_token_info_edit.html.hover.right="secretPopoverText">
                  <icon name="info-circle"></icon>
                </span>
                </label>
                <div class="col-7 col-xl-8">
                  <input type="text"
                         id="webhook-secret"
                         class="form-control"
                         :class="{'is-invalid': showWebhookSecretInvalidMessage}"
                         v-model="webhook.secret">
                  <div v-show="showWebhookSecretInvalidMessage" class="invalid-feedback">
                    Please provide a valid secret.
                  </div>
                </div>
              </div>

              <div class="row form-group" v-show="considerSsl()">
                <label for="webhook-ssl" class="col-4 col-xl-3 col-form-label text-muted">
                  SSL Verification:
                  <span id="webhook_ssl_info_edit" v-b-popover:webhook_ssl_info_edit.html.hover.right="sslPopoverText">
                  <icon name="info-circle"></icon>
                </span>
                </label>
                <div class="col-8 col-xl-9">
                  <input type="checkbox" id="webhook-ssl" v-model="webhook.ssl_verification">
                </div>
              </div>

              <button class="btn btn-success" v-busy-button="promiseUpdateWebhook">
                Save
              </button>
            </form>
          </div>

          <div v-else>
            <field class="mb-3">
              <div slot="label">
                Webhook URL:
                <span id="webhook_url_info" v-b-popover:webhook_url_info.html.hover.right="webhookUrlPopoverText">
                <icon name="info-circle"></icon>
              </span>
              </div>
              <div slot="value">{{webhook.url}}</div>
            </field>

            <field class="mb-3 align-items-center">
              <div slot="label">
                Secret Token:
                <span id="secret_token_info" v-b-popover:secret_token_info.html.hover.right="secretPopoverText">
                <icon name="info-circle"></icon>
              </span>
              </div>
              <div slot="value" class="row">
                <sensitive-field class="col-10" ref="webhook-secret" v-if="webhook.secret" :text="webhook.secret" readonly/>
                <copy-button :value="webhook.secret" @onSuccess="onCopy" :title="'Copy'" class="text-nowrap p-0"/>
              </div>
            </field>

            <field class="mb-3" v-if="considerSsl()">
              <div slot="label">
                SSL Verification:
                <span id="webhook_ssl_info" v-b-popover:webhook_ssl_info.html.hover.right="sslPopoverText">
                  <icon name="info-circle"></icon>
                </span>
              </div>
              <div slot="value">
              <span v-if="webhook.ssl_verification" class="text-success">
                Enabled
              </span>
                <span v-else class="text-danger">
                Disabled
              </span>
              </div>
            </field>
          </div>
        </div>
        <div class="col-2">
          <toggle-edit class="d-flex justify-content-end"
                       :editMode="editMode"
                       @click="toggleEditMode"></toggle-edit>
        </div>
      </div>
    </div>

    <modal-delete-webhook
      :modalId="modalId"
      @webhookCleared="webhookCleared"
      :channel="channel">
    </modal-delete-webhook>
  </div>
</template>

<script>
  import SensitiveField from '@/components/common/SensitiveField'
  import ToggleEditBtn from '@/components/administration/common/ToggleEditButton'
  import Field from '@/components/administration/common/Field'
  import ErrorsAlert from '@/components/administration/common/ErrorsAlert'
  import ConfirmDeleteWebhook from '@/components/administration/channel/webhook/ConfirmDeleteWebhook'
  import {prepareChannel} from '@/components/administration/channel/router-guard-handler'
  import eventBus from '@/services/event-bus/event-bus'
  import channelService from '@/services/amio-api/channel.service'
  import 'vue-awesome/icons/info-circle'
  import 'vue-awesome/icons/trash'
  import clone from 'ramda/src/clone'
  import EmptyWebhookView from '../../../../../components/administration/channel/webhook/EmptyWebhookView'
  import GeneratePassword from 'generate-password'
  import CopyButton from '../../../../../components/common/CopyButton'

  const secretPopoverText = 'Use this secret token to calculate X-Hub-Signature to ensure the request came from Amio servers. ' +
    'For more details see ' +
    '<a href="https://docs.amio.io/v1.0/reference#security"  target="_blank">docs</a>.'

  const sslPopoverText = 'By default, we verify SSL certificates when delivering payloads. This ensures that data ' +
    'are securely delivered to your URL endpoint. Disabling this option is <b>not recommended</b>.'

  const webhookUrlPopoverText = 'URL where Amio will send ' +
    '<a href="https://docs.amio.io/v1.0/reference#webhooks" target="_blank">webhooks</a>. ' +
    'If you want to receive webhooks locally, see ' +
    '<a href="https://docs.amio.io/v1.0/docs/ngrok" target="_blank">docs</a>.'

  export default {
    components: {
      CopyButton,
      EmptyWebhookView,
      'field': Field,
      'errors-alert': ErrorsAlert,
      'toggle-edit': ToggleEditBtn,
      'modal-delete-webhook': ConfirmDeleteWebhook,
      'sensitive-field': SensitiveField
    },

    store: {
      channel: 'administration.channelDetail.channel'
    },

    data() {
      return {
        reload: null,
        counter: 0,
        webhook: {},
        promiseUpdateWebhook: null,
        errors: [],
        editMode: false,
        showWebhookUrlInvalidMessage: false,
        showWebhookSecretInvalidMessage: false,
        modalId: 'clear-webhook-modal',
        secretPopoverText,
        sslPopoverText,
        webhookUrlPopoverText
      }
    },

    methods: {
      toggleEditMode() {
        this.errors = []
        this.editMode = !this.editMode
        this.clearError()
        this.webhook = this.channel.webhook ? clone(this.channel.webhook) : {ssl_verification: true}
      },

      saveWebhook() {
        if (!this.checkFormErrors()) return

        const data = {
          webhook: {
            url: this.webhook.url
          }
        }

        if (this.webhook.secret) {
          data.webhook.secret = this.webhook.secret
        }

        if (this.considerSsl()) {
          data.webhook.ssl_verification = this.webhook.ssl_verification
        }
        return channelService
          .patch(this.channel.id, data, [422])
          .then(response => {
            eventBus.$emit('update-channel', response.data)
            this.errors = []
          })
          .then(this.toggleEditMode)
          .catch((error) => {
            if (error.response.status !== 422) return

            log.debug('validation error', error)
            this.errors = error.response.data.errors
            return this.handleError(this.errors[0])
          })
          .then(this.clearUpdateWebhookPromise)
      },

      checkFormErrors() {
        this.clearError()
        this.showWebhookUrlInvalidMessage = !(this.webhook.url)
        this.showWebhookSecretInvalidMessage = !(this.webhook.secret)

        return this.webhook.url && this.webhook.secret
      },

      handleError(error) {
        if (error.field === 'webhook.url') {
          this.showWebhookUrlInvalidMessage = true
        } else {
          this.showWebhookSecretInvalidMessage = true
        }
      },

      webhookCleared() {
        this.channel.webhook = {}
        this.toggleEditMode()
      },

      clearError() {
        this.showWebhookUrlInvalidMessage = false
        this.showWebhookSecretInvalidMessage = false
        this.errors = []
      },

      clearUpdateWebhookPromise() {
        this.promiseUpdateWebhook = null
      },

      considerSsl() {
        return this.webhook.url && this.webhook.url.startsWith('https')
      },

      createWebhook() {
        this.reload = (this.reload === 0) ? null : 0
        this.errors = []
        this.editMode = true
        this.clearError()
        this.webhook.secret = GeneratePassword.generate({
          length: 30,
          numbers: true,
          lowercase: true,
          uppercase: true,
          strict: true
        })
      },
      onCopy() {
        this.$refs['webhook-secret'].selectText()
      }
    },

    beforeRouteEnter(to, from, next) {
      prepareChannel(to, next, (vm, channel) => {
        vm.webhook = channel.webhook || {}
      })
    }
  }
</script>
